import React from 'react';
import { getActivityStats } from '../../activities/activityTypes';
import {
  FIELD_KEYS,
  pick,
  isTruthy,
  DetailTopBar,
  DetailHero,
  DetailTitleSheet,
  WeatherPlaceholder,
  MideBlock,
} from './ActivityDetailPage';

// Senderismo-specific field keys (on top of the shared ones).
const SEND_KEYS = {
  familiar: ['familiar', 'family', 'aptaNinos', 'aptoNinos', 'conNinos', 'kids'],
  routeShape: ['routeShape', 'shape', 'recorrido', 'circular', 'tipoRecorrido'],
  profile: ['profile', 'elevationProfile', 'altitudeProfile', 'perfil', 'elevations', 'altitudes'],
  maxAlt: ['maxAltitude', 'cotaMaxima', 'altMax', 'maxAlt'],
  minAlt: ['minAltitude', 'cotaMinima', 'altMin', 'minAlt'],
};

/**
 * Builds an SVG polyline path (700×150 viewBox) from a numeric elevation
 * series. Returns null when there aren't at least two points.
 */
function buildProfilePath(points) {
  const nums = (Array.isArray(points) ? points : [])
    .map(Number)
    .filter(Number.isFinite);
  if (nums.length < 2) return null;
  const max = Math.max(...nums);
  const min = Math.min(...nums);
  const span = max - min || 1;
  const stepX = 700 / (nums.length - 1);
  const pts = nums.map((v, i) => [
    +(i * stepX).toFixed(1),
    +(146 - ((v - min) / span) * 120).toFixed(1),
  ]);
  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x} ${y}`).join(' ');
  const area = `M0 146 ${line.slice(1)} L700 146 Z`;
  return { line, area, max, min };
}

/**
 * Senderismo detail page. Follows the v3 "01 · Senderismo" design: cota belt,
 * hero, beveled title, description + altitude profile on the left, and a data
 * grid (distancia / desnivel / tiempo / familiar) + MIDE + weather on the right.
 * Any datum not present in the activity is omitted (e.g. no profile → no chart).
 */
function SenderismoDetail({ activity }) {
  const values = activity.values || {};
  const description = pick(values, FIELD_KEYS.description);
  const place = pick(values, FIELD_KEYS.location);
  const region = pick(values, FIELD_KEYS.region);
  const gpx = pick(values, FIELD_KEYS.gpx);
  const routeShape = pick(values, SEND_KEYS.routeShape);
  const familiar = pick(values, SEND_KEYS.familiar);

  const stats = getActivityStats(activity);
  const dist = stats.find((s) => s.unit === 'km');
  const gain = stats.find((s) => s.unit === 'D+');
  const time = stats.find((s) => s.unit === 'horas');
  const mide = stats.find((s) => s.kind === 'mide' && s.present);
  const mideParts = mide ? String(mide.value).split('-') : [];

  const tiles = [
    dist?.present && { key: 'dist', label: 'DISTANCIA', value: dist.value, unit: 'km', Icon: DistanceIcon },
    gain?.present && { key: 'gain', label: 'DESNIVEL +', value: gain.value, unit: 'm', Icon: GainIcon },
    time?.present && { key: 'time', label: 'TIEMPO', value: time.value, unit: 'h', Icon: TimeIcon },
  ].filter(Boolean);

  const profile = buildProfilePath(pick(values, SEND_KEYS.profile));
  const maxAlt = pick(values, SEND_KEYS.maxAlt) ?? profile?.max;
  const minAlt = pick(values, SEND_KEYS.minAlt) ?? profile?.min;

  return (
    <div className="Detail">
      <DetailTopBar
        activity={activity}
        rightCell={gpx && <span className="Detail__barCell Detail__barCell--gpx">GPX</span>}
      />

      <DetailHero
        activity={activity}
        chips={
          routeShape && (
            <span className="Detail__chip Detail__chip--accent">
              {isTruthy(routeShape) ? 'RUTA CIRCULAR' : String(routeShape).toUpperCase()}
            </span>
          )
        }
      />

      <DetailTitleSheet
        eyebrow={[place && String(place).toUpperCase(), region && String(region).toUpperCase()]
          .filter(Boolean)
          .join(' · ')}
        title={activity.name}
        actions={
          <>
            <span className="Detail__action Detail__action--gold" aria-hidden="true"><StarIcon /></span>
            {gpx && (
              <a className="Detail__action" href={gpx} download aria-label="Descargar GPX"><DownloadIcon /></a>
            )}
          </>
        }
      />

      <div className="Detail__body">
        <div className="Detail__col">
          {description && (
            <section className="Detail__section">
              <div className="Detail__sectionLabel">DESCRIPCIÓN</div>
              {String(description)
                .split(/\n{2,}/)
                .map((para, i) => (
                  <p key={i} className="Detail__desc">{para}</p>
                ))}
            </section>
          )}

          {profile && (
            <div className="Detail__profile">
              <div className="Detail__profileHead">
                <span>PERFIL DE ALTITUD</span>
                <span>MÁX {maxAlt} M · MÍN {minAlt} M</span>
              </div>
              <svg viewBox="0 0 700 150" width="100%" height="150" fill="none" preserveAspectRatio="none">
                <path d={profile.area} fill="#16281E" />
                <path d={profile.line} stroke="#F5B72A" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
                <path d="M0 146 L700 146" stroke="#2E4A38" strokeWidth="1" />
              </svg>
            </div>
          )}
        </div>

        <div className="Detail__col">
          {(tiles.length > 0 || familiar) && (
            <div className="Detail__stats">
              {tiles.map(({ key, label, value, unit, Icon }) => (
                <div key={key} className="Detail__stat">
                  <span className="Detail__statLabel"><Icon />{label}</span>
                  <span className="Detail__statVal">
                    {value} <em className="Detail__statUnit">{unit}</em>
                  </span>
                </div>
              ))}
              {familiar && (
                <div className="Detail__stat Detail__stat--accent">
                  <span className="Detail__statLabel"><FamilyIcon />FAMILIAR</span>
                  <span className="Detail__statFamiliar">
                    {isTruthy(familiar) ? 'Apta con niños' : String(familiar)}
                  </span>
                </div>
              )}
            </div>
          )}

          {mide && <MideBlock parts={mideParts} />}

          <WeatherPlaceholder />
        </div>
      </div>

      {/* Mobile-only sticky footer CTA (design "SIGUIENTE PASO · DESCARGAR GPX"). */}
      {gpx && (
        <a className="Detail__gpxFooter" href={gpx} download>
          <span className="Detail__gpxFooterText">
            <span>SIGUIENTE PASO</span>
            <strong>DESCARGAR GPX</strong>
          </span>
          <span className="Detail__gpxFooterIcon" aria-hidden="true"><DownloadIcon /></span>
        </a>
      )}
    </div>
  );
}

/* ---- Inline icons (24×24, stroke inherits currentColor) ---- */
const svg = (d) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="butt" strokeLinejoin="miter" aria-hidden="true">
    <path d={d} />
  </svg>
);
const DistanceIcon = () => svg('M2 12 L22 12 M2 8 L2 16 M22 8 L22 16 M12 10 L12 14');
const GainIcon = () => svg('M2 20 L22 20 M4 17 L10 7 L14 13 L20 4 M20 4 L20 9 M20 4 L15 4');
const TimeIcon = () => svg('M4 4 L20 4 L20 20 L4 20 Z M12 8 L12 12 L16 12');
const FamilyIcon = () => svg('M2 2 L6 2 L6 6 L2 6 Z M4 6 L4 12 M2 12 L6 12 M2 12 L2 20 M6 12 L6 20 M18 2 L22 2 L22 6 L18 6 Z M20 6 L20 12 M18 12 L22 12 M18 12 L18 20 M22 12 L22 20 M10 10 L14 10 L14 14 L10 14 Z M12 14 L12 20 M2 22 L22 22');
const StarIcon = () => svg('M12 3 L15 9 L21 10 L16 15 L18 21 L12 18 L6 21 L8 15 L3 10 L9 9 Z');
const DownloadIcon = () => svg('M12 4 L12 16 M6 11 L12 17 L18 11 M4 20 L20 20');

export default SenderismoDetail;
